import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Loader2, X } from "lucide-react";
import { CORE_SERVICES, MORE_SERVICES } from "../../data/services";
import { servicePath } from "@/lib/paths";
import { ViduraSearchAnswer } from "./ViduraSearchAnswer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATIC_PAGES = [
  { title: "Bill Optimiser — free BESS sizing", href: "/besssizing" },
  { title: "Live Tenders", href: "/tenders" },
  { title: "Blogs & Insights", href: "/insights" },
  { title: "News", href: "/news" },
  { title: "Events", href: "/events" },
  { title: "Case Studies", href: "/casestudies" },
  { title: "About Us", href: "/aboutus" },
  { title: "Our Team", href: "/team" },
  { title: "Impact", href: "/impact" },
  { title: "FAQ", href: "/faq" },
  { title: "Careers", href: "/careers" },
  { title: "Contact Us", href: "/contactus" },
  { title: "Gallery", href: "/gallery" },
  { title: "Client Portal — login", href: "/clientportal" },
  { title: "Team Portal — login", href: "/teamportal" },
  ...[...CORE_SERVICES, ...MORE_SERVICES].map((s) => ({ title: `${s.short} — service`, href: servicePath(s.slug) })),
];
export const PAGES = STATIC_PAGES;
export const searchPages = (ql) =>
  PAGES.filter((p) => p.title.toLowerCase().includes(ql)).slice(0, 6)
    .map((p) => ({ type: p.href.startsWith("/coreservices") ? "Service" : "Page", ...p }));

const TYPE_CLS = { Page: "text-gf-primary", Service: "text-emerald-400", Blog: "text-sky-400", Tender: "text-amber-400", News: "text-fuchsia-400", "Case study": "text-orange-400", Event: "text-gf-muted" };

const RECENT_KEY = "gf_recent_searches";
const getRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; } };
const saveRecent = (term) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify([term, ...getRecent().filter((x) => x !== term)].slice(0, 5))); } catch { /* ignore */ }
};

export const SiteSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [remote, setRemote] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const [recent, setRecent] = useState([]);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); setQ(""); }, [location.pathname]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", key); };
  }, []);
  useEffect(() => { if (open) { setRecent(getRecent()); setTimeout(() => inputRef.current?.focus(), 60); } }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setRemote([]); setBusy(false); return; }
    setBusy(true);
    const t = setTimeout(() => {
      setErr(false);
      fetch(`${API}/site-search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then((d) => setRemote(Array.isArray(d) ? d : []))
        .catch(() => { setErr(true); setRemote([]); })
        .finally(() => setBusy(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const ql = q.trim().toLowerCase();
  const remoteHrefs = new Set(remote.map((r) => r.href));
  const local = ql.length >= 2 && remote.length === 0 ? searchPages(ql).filter((p) => !remoteHrefs.has(p.href)) : [];
  const suggest = remote.find((r) => r.type === "Suggest");
  const results = [...remote.filter((r) => r.type !== "Suggest"), ...local];

  const submit = () => {
    if (ql.length < 2) return;
    saveRecent(q.trim());
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid="site-search-toggle" aria-label="Search the website"
        className="p-2 text-gf-muted hover:text-gf-primary transition-colors">
        {open ? <X className="w-[18px] h-[18px]" /> : <Search className="w-[18px] h-[18px]" />}
      </button>
      {open && (
        <div data-testid="site-search-panel"
          className="absolute right-0 top-full mt-3 w-[min(420px,88vw)] bg-gf-bg/95 backdrop-blur-xl border border-white/15 shadow-2xl z-50">
          <div className="relative border-b border-white/10">
            {busy
              ? <Loader2 className="w-4 h-4 text-gf-muted absolute left-3.5 top-1/2 -translate-y-1/2 animate-spin" />
              : <Search className="w-4 h-4 text-gf-muted absolute left-3.5 top-1/2 -translate-y-1/2" />}
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="site-search-input"
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Search blogs, tenders, news, services, pages…"
              className="w-full bg-transparent pl-10 pr-4 py-3.5 font-body text-sm text-gf-text placeholder:text-gf-muted focus:outline-none" />
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {ql.length < 2 && recent.length > 0 && (
              <div className="px-4 py-3 border-b border-white/5" data-testid="site-search-recent">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[8px] uppercase tracking-wider text-gf-muted">Recent searches</p>
                  <button onClick={() => { try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ } setRecent([]); }}
                    data-testid="site-search-recent-clear" className="font-mono text-[8px] uppercase text-gf-muted hover:text-red-400">Clear</button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {recent.map((r) => (
                    <button key={r} onClick={() => setQ(r)} data-testid={`site-search-recent-${r}`}
                      className="font-body text-xs text-gf-text border border-white/15 px-2.5 py-1 hover:border-gf-primary/50 hover:text-gf-primary transition-colors">
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {ql.length < 2 && recent.length === 0 && (
              <p className="font-body text-xs text-gf-muted px-4 py-5" data-testid="site-search-hint">
                Type at least 2 characters — searches blogs, tenders, news, case studies, events, services and pages.
              </p>
            )}
            {ql.length >= 2 && !busy && results.length === 0 && (
              err ? (
                <p className="font-body text-xs text-amber-400 px-4 py-5" data-testid="site-search-error">Search is taking a moment — <button className="underline" onClick={() => setQ((v) => v + " ")}>retry</button> or press Enter to open the full search page.</p>
              ) : (
                <div>
                  <p className="font-body text-xs text-gf-muted px-4 pt-4 pb-2" data-testid="site-search-empty">Nothing matched "{q}" directly — asking Vidura…</p>
                  <ViduraSearchAnswer q={q.trim()} compact onNavigate={() => { saveRecent(q.trim()); setOpen(false); }} />
                </div>
              )
            )}
            {results.map((r, i) => (
              <Link key={i} to={r.href} data-testid={`site-search-result-${i}`} onClick={() => saveRecent(q.trim())}
                className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 hover:bg-white/[0.05] transition-colors">
                <span className={`font-mono text-[8px] uppercase tracking-wider w-[70px] shrink-0 ${TYPE_CLS[r.type] || "text-gf-muted"}`}>{r.type}</span>
                <span className="font-body text-[13px] text-gf-text leading-snug line-clamp-2">{r.title}{r.section && <span className="font-mono text-[9px] text-gf-muted ml-2">{r.section}</span>}</span>
              </Link>
            ))}
            {suggest && results.length > 0 && <p className="font-body text-[11px] text-gf-muted px-4 py-2" data-testid="site-search-suggest">{suggest.title}</p>}
          </div>
        </div>
      )}
    </div>
  );
};
